/**
 * Flattens a nested object into a flat object with keys representing the path to each value.
 *
 * @param {Object} params - The parameters for the function.
 * @param {Object} params.obj - The object to flatten. Default is an empty object.
 * @param {string} params.parentKey - The accumulated key path for recursion. Default is an empty string.
 * @param {Object} params.result - The resulting flat object. Default is an empty object.
 * @param {Object} params.opts - Options for the function.
 * @param {boolean} params.opts.includeAllKeys - When true, includes all keys in the flat object, not just the terminal ones. Default is false.
 * @returns {Object} - The flattened object.
 *
 * @example
 *
 * const input = {
 *   credentials: {
 *     some: 'test',
 *     custom: {
 *       custom: 'value',
 *       'some.test': 'val'
 *     }
 *   },
 *   list: [1, { deep: 'value' }, 3]
 * };
 *
 * const options = { includeAllKeys: false };
 *
 * const flatResult = flattenObjectKeys({ obj: input, opts: options });
 * console.log(flatResult);
 *
 * // Output:
 * // {
 * //   'credentials.some': 'test',
 * //   'credentials.custom.custom': 'value',
 * //   'credentials.custom.some.test': 'val',
 * //   'list[0]': 1,
 * //   'list[1].deep': 'value',
 * //   'list[2]': 3
 * // }
 */

// Helper function to check if a value is a React element
const isReactElement = (value) => {
  // Check if React is available and the value is a React element
  if (typeof value === 'object' && value !== null && value.$$typeof) {
    // React elements have a $$typeof property with Symbol.for('react.element')
    const REACT_ELEMENT_TYPE = typeof Symbol === 'function' && Symbol.for ? Symbol.for('react.element') : 0xeac7;
    return value.$$typeof === REACT_ELEMENT_TYPE;
  }
  return false;
};

export const flattenObjectKeys = ({ obj = {}, parentKey = '', result = {}, opts = { includeAllKeys: false } }) => {
  for (const [key, value] of Object.entries(obj || {})) {
    // Skip keys named '__meta'
    if (key === '__meta') {
      continue;
    }

    // Skip React elements to prevent infinite recursion
    if (isReactElement(value)) {
      continue;
    }

    // Construct new key path
    const newKey = parentKey ? `${parentKey}.${key}` : key;

    // Include all keys if the option is set
    if (opts.includeAllKeys) {
      result[newKey] = value;
    }

    // Recursively flatten nested objects
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      flattenObjectKeys({ obj: value, parentKey: newKey, result, opts });
    } else if (Array.isArray(value)) {
      // Handle arrays
      value.forEach((item, index) => {
        // Skip React elements in arrays too
        if (isReactElement(item)) {
          return;
        }

        if (opts.includeAllKeys) {
          result[`${newKey}[${index}]`] = item;
        }
        if (typeof item === 'object' && item !== null) {
          flattenObjectKeys({ obj: item, parentKey: `${newKey}[${index}]`, result, opts });
        } else {
          result[`${newKey}[${index}]`] = item;
        }
      });
    } else {
      // Add the final key-value pair to the result
      result[newKey] = value;
    }
  }
  return result;
};
